import omit from 'lodash/omit';

import { isBookingProcessAlias } from '../../transactions/transaction';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  getStartOfWeek,
  monthIdString,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';
import { storableError } from '../../util/errors';
import { uniqueBy } from '../../util/generators';
import * as log from '../../util/log';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { parse } from '../../util/urlHelpers';

import { FREE_BOOKING_LISTING_TYPE } from '../../config/configListing';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  fetchStripeAccount,
  updateStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser, currentUserShowSuccess, updateUserEvents } from '../../ducks/user.duck';
import { UserEvents } from '../../ducks/UserEvents';
import { getCommissionInfo, notifyAlternativePayoutApi } from '../../util/api';
import {
  alternativePayoutFromFormValues,
  ALTERNATIVE_PAYOUT_UPDATE_QUERY,
  buildAlternativePayoutProfileUpdate,
  buildUserStripePayoutChoiceUpdate,
  formatAlternativePayoutForEmail,
} from '../../util/alternativePayout';
import { showToast } from '../../util/toast';
import { withOnlineMeetingPrivacy } from '../../util/onlineMeeting';
import { isLinkableOwnListing } from '../../components/MyListingsSection/MyListingsSection.helpers';
import { endOfAvailabilityExceptionRange } from './EditListingWizard/EditListingAvailabilityPanel/availability.helpers';

const { UUID, Money } = sdkTypes;

// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are probably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
        uploadedImages: {},
        uploadedImagesOrder: [],
      }
    : {
        uploadedImages,
        uploadedImagesOrder,
      };
};

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

// When navigating through weekly calendar,
// we want to merge new week-related data (inProgres, error) to weeklyExceptionQueries hashmap.
const mergeToWeeklyExceptionQueries = (weeklyExceptionQueries, weekStartId, newDataProps) => {
  return weekStartId
    ? {
        weeklyExceptionQueries: {
          ...weeklyExceptionQueries,
          [weekStartId]: {
            ...weeklyExceptionQueries[weekStartId],
            ...newDataProps,
          },
        },
      }
    : {};
};
// When navigating through monthly calendar (e.g. when adding a new AvailabilityException),
// we want to merge new month-related data (inProgres, error) to monthlyExceptionQueries hashmap.
const mergeToMonthlyExceptionQueries = (monthlyExceptionQueries, monthId, newDataProps) => {
  return monthId
    ? {
        monthlyExceptionQueries: {
          ...monthlyExceptionQueries,
          [monthId]: {
            ...monthlyExceptionQueries[monthId],
            ...newDataProps,
          },
        },
      }
    : {};
};

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => payload => ({ type: actionType, payload, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';
export const CLEAR_PUBLISH_ERROR = 'app/EditListingPage/CLEAR_PUBLISH_ERROR';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/EditListingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/EditListingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/EditListingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/EditListingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/EditListingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/EditListingPage/PUBLISH_LISTING_ERROR';

export const COMMISSION_INFO_REQUEST = 'app/EditListingPage/COMMISSION_INFO';
export const COMMISSION_INFO_SUCCESS = 'app/EditListingPage/COMMISSION_INFO_SUCCESS';
export const COMMISSION_INFO_ERROR = 'app/EditListingPage/COMMISSION_INFO_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';
export const FETCH_EXTRA_EXCEPTIONS_SUCCESS =
  'app/EditListingPage/FETCH_EXTRA_AVAILABILITY_EXCEPTIONS_SUCCESS';

export const ADD_EXCEPTION_REQUEST = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_REQUEST';
export const ADD_EXCEPTION_SUCCESS = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS';
export const ADD_EXCEPTION_ERROR = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const SET_STOCK_REQUEST = 'app/EditListingPage/SET_STOCK_REQUEST';
export const SET_STOCK_SUCCESS = 'app/EditListingPage/SET_STOCK_SUCCESS';
export const SET_STOCK_ERROR = 'app/EditListingPage/SET_STOCK_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_ERROR';
export const SAVE_PAYOUT_DETAILS_IDLE = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_IDLE';

export const FETCH_PACKAGE_OWN_LISTINGS_REQUEST =
  'app/EditListingPage/FETCH_PACKAGE_OWN_LISTINGS_REQUEST';
export const FETCH_PACKAGE_OWN_LISTINGS_SUCCESS =
  'app/EditListingPage/FETCH_PACKAGE_OWN_LISTINGS_SUCCESS';
export const FETCH_PACKAGE_OWN_LISTINGS_ERROR =
  'app/EditListingPage/FETCH_PACKAGE_OWN_LISTINGS_ERROR';

export const PACKAGE_FANOUT_SUCCESS = 'app/EditListingPage/PACKAGE_FANOUT_SUCCESS';
export const PACKAGE_FANOUT_ERROR = 'app/EditListingPage/PACKAGE_FANOUT_ERROR';
export const PACKAGE_FANOUT_RETRY_REQUEST = 'app/EditListingPage/PACKAGE_FANOUT_RETRY_REQUEST';

const PACKAGE_OWN_LISTINGS_PAGE_SIZE = 20;

// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  commissionInfoInProgress: false,
  commissionInfo: {},
  packageOwnListings: [],
  packageOwnListingsInProgress: false,
  packageOwnListingsHasMore: false,
  packageOwnListingsPage: 1,
  packageOwnListingsKeywords: '',
  packageFanoutFailedTitles: null,
  packageFanoutRetryInProgress: false,
  pendingPackageSync: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };
    case CLEAR_PUBLISH_ERROR:
      return { ...state, publishListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        listingId: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishListingError: {
          listingId: state.listingId,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        updateInProgress: false,
        // availabilityCalendar: { ...state.availabilityCalendar },
      };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data.id;
      const { listingId, allExceptions, weeklyExceptionQueries, monthlyExceptionQueries } = state;
      // If listing stays the same, we trust previously fetched exception data.
      return listingIdFromPayload?.uuid === state.listingId?.uuid
        ? {
            ...initialState,
            listingId,
            allExceptions,
            weeklyExceptionQueries,
            monthlyExceptionQueries,
          }
        : { ...initialState, listingId: listingIdFromPayload };
    }
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case FETCH_EXCEPTIONS_REQUEST: {
      const { monthId, weekStartId } = payload.params;
      const newData = { fetchExceptionsError: null, fetchExceptionsInProgress: true };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
        ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
        : {};
      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_SUCCESS: {
      const { exceptions, monthId, weekStartId } = payload;
      const combinedExceptions = state.allExceptions.concat(exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      const newData = { fetchExceptionsInProgress: false };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
        ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
        : {};
      return { ...state, allExceptions, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_ERROR: {
      const { monthId, weekStartId, error } = payload;
      const newData = { fetchExceptionsInProgress: false, fetchExceptionsError: error };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
        ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
        : {};

      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXTRA_EXCEPTIONS_SUCCESS: {
      const combinedExceptions = state.allExceptions.concat(payload.exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      // TODO: currently we don't handle thrown errors from these paginated calls
      return { ...state, allExceptions };
    }
    case ADD_EXCEPTION_REQUEST:
      return {
        ...state,
        addExceptionError: null,
        addExceptionInProgress: true,
      };
    case ADD_EXCEPTION_SUCCESS: {
      const exception = payload;
      const combinedExceptions = state.allExceptions.concat(exception);
      const allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
      return {
        ...state,
        allExceptions,
        addExceptionInProgress: false,
      };
    }
    case ADD_EXCEPTION_ERROR:
      return {
        ...state,
        addExceptionError: payload.error,
        addExceptionInProgress: false,
      };

    case DELETE_EXCEPTION_REQUEST:
      return {
        ...state,
        deleteExceptionError: null,
        deleteExceptionInProgress: true,
      };
    case DELETE_EXCEPTION_SUCCESS: {
      const exception = payload;
      const id = exception.id.uuid;
      const allExceptions = state.allExceptions.filter(e => e.id.uuid !== id);
      return {
        ...state,
        allExceptions,
        deleteExceptionInProgress: false,
      };
    }
    case DELETE_EXCEPTION_ERROR:
      return {
        ...state,
        deleteExceptionError: payload.error,
        deleteExceptionInProgress: false,
      };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, ...rest } };
      return { ...state, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
      const uploadedImages = omit(state.uploadedImages, id);
      return { ...state, uploadedImagesOrder, uploadedImages, uploadImageError: error };
    }

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);

      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case SET_STOCK_REQUEST:
      return { ...state, setStockInProgress: true, setStockError: null };
    case SET_STOCK_SUCCESS:
      return { ...state, setStockInProgress: false };
    case SET_STOCK_ERROR:
      return { ...state, setStockInProgress: false, setStockError: payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };
    case SAVE_PAYOUT_DETAILS_IDLE:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: false };
    case COMMISSION_INFO_REQUEST:
      return { ...state, commissionInfoInProgress: true };
    case COMMISSION_INFO_SUCCESS:
      return { ...state, commissionInfo: payload, commissionInfoInProgress: false };
    case COMMISSION_INFO_ERROR:
      return { ...state, commissionInfoInProgress: false };

    case FETCH_PACKAGE_OWN_LISTINGS_REQUEST:
      return {
        ...state,
        packageOwnListingsInProgress: true,
      };
    case FETCH_PACKAGE_OWN_LISTINGS_SUCCESS: {
      const { listings, append, page, keywords, hasMore } = payload;
      return {
        ...state,
        packageOwnListingsInProgress: false,
        packageOwnListings: append ? [...(state.packageOwnListings || []), ...listings] : listings,
        packageOwnListingsHasMore: hasMore,
        packageOwnListingsPage: page,
        packageOwnListingsKeywords: keywords,
      };
    }
    case FETCH_PACKAGE_OWN_LISTINGS_ERROR:
      return { ...state, packageOwnListingsInProgress: false };

    case PACKAGE_FANOUT_SUCCESS:
      return {
        ...state,
        packageFanoutFailedTitles: null,
        packageFanoutRetryInProgress: false,
        pendingPackageSync: null,
      };
    case PACKAGE_FANOUT_ERROR:
      return {
        ...state,
        packageFanoutFailedTitles: payload?.titles || null,
        packageFanoutRetryInProgress: false,
        pendingPackageSync: payload?.packageSync || state.pendingPackageSync,
      };
    case PACKAGE_FANOUT_RETRY_REQUEST:
      return { ...state, packageFanoutRetryInProgress: true };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

export const clearPublishError = () => ({
  type: CLEAR_PUBLISH_ERROR,
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraftRequest = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListingRequest = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

export const requestCommissionInfoRequest = requestAction(COMMISSION_INFO_REQUEST);
export const requestCommissionInfoSuccess = successAction(COMMISSION_INFO_SUCCESS);
export const requestCommissionInfoError = errorAction(COMMISSION_INFO_ERROR);

// SDK method: ownListings.update
export const updateListingRequest = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

const asListingUuid = id => (id?.uuid ? id : new UUID(id));

const listingTitleFromState = (state, listingId) => {
  const uuid = listingId?.uuid || listingId;
  const fromPicker = (state.EditListingPage?.packageOwnListings || []).find(
    listing => listing?.id?.uuid === uuid
  );
  if (fromPicker?.attributes?.title) {
    return fromPicker.attributes.title;
  }
  const own = state.marketplaceData?.entities?.ownListing?.[uuid];
  return own?.attributes?.title || uuid;
};

export const fetchPackageOwnListings = ({ page = 1, keywords, append = false } = {}) => (
  dispatch,
  getState,
  sdk
) => {
  const state = getState().EditListingPage;
  // Preserve the active search on load-more; only clear when keywords is explicitly passed.
  const trimmedKeywords =
    typeof keywords === 'string'
      ? keywords.trim()
      : append
      ? state.packageOwnListingsKeywords || ''
      : '';
  const nextPage = append ? (state.packageOwnListingsPage || 1) + 1 : page;
  dispatch({ type: FETCH_PACKAGE_OWN_LISTINGS_REQUEST });

  if (typeof sdk?.ownListings?.query !== 'function') {
    dispatch({ type: FETCH_PACKAGE_OWN_LISTINGS_ERROR });
    return Promise.resolve(null);
  }

  return sdk.ownListings
    .query({
      states: ['published', 'draft', 'pendingApproval'],
      page: nextPage,
      perPage: PACKAGE_OWN_LISTINGS_PAGE_SIZE,
      'fields.listing': ['title', 'state', 'price', 'publicData', 'privateData', 'createdAt'],
      ...(trimmedKeywords ? { keywords: trimmedKeywords } : {}),
    })
    .then(response => {
      const listings = denormalisedResponseEntities(response).filter(isLinkableOwnListing);
      const meta = response?.data?.meta || {};
      const totalPages = Number(meta.totalPages) || 1;
      dispatch({
        type: FETCH_PACKAGE_OWN_LISTINGS_SUCCESS,
        payload: {
          listings,
          append,
          page: nextPage,
          keywords: trimmedKeywords,
          hasMore: nextPage < totalPages,
        },
      });
      return response;
    })
    .catch(e => {
      log.error(e, 'fetch-package-own-listings-failed', {});
      dispatch({ type: FETCH_PACKAGE_OWN_LISTINGS_ERROR, payload: storableError(e) });
    });
};

const runPackageFanout = (sdk, packageSync) => {
  const siblingListingIds = packageSync?.siblingListingIds || [];
  const removedListingIds = packageSync?.removedListingIds || [];
  const packages = packageSync?.packages ?? null;
  const updates = [
    ...siblingListingIds.map(listingId => ({ listingId, packages })),
    ...removedListingIds.map(listingId => ({ listingId, packages: null })),
  ];
  if (updates.length === 0) {
    return Promise.resolve([]);
  }
  return Promise.all(
    updates.map(({ listingId, packages: pkgs }) =>
      sdk.ownListings
        .update({ id: asListingUuid(listingId), publicData: { packages: pkgs } })
        .then(() => null)
        .catch(error => ({ listingId, error }))
    )
  ).then(results => results.filter(Boolean));
};

export const syncListingPackages = packageSync => (dispatch, getState, sdk) => {
  return runPackageFanout(sdk, packageSync).then(failed => {
    if (failed.length) {
      const titles = failed
        .map(item => listingTitleFromState(getState(), item.listingId))
        .join(', ');
      dispatch({
        type: PACKAGE_FANOUT_ERROR,
        payload: { titles, packageSync },
      });
    } else {
      dispatch({ type: PACKAGE_FANOUT_SUCCESS });
    }
    return failed;
  });
};

export const retryPackageFanout = () => (dispatch, getState) => {
  const packageSync = getState().EditListingPage?.pendingPackageSync;
  if (!packageSync) {
    return Promise.resolve([]);
  }
  dispatch({ type: PACKAGE_FANOUT_RETRY_REQUEST });
  return dispatch(syncListingPackages(packageSync));
};

// SDK method: ownListings.show
export const showListingsRequest = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// SDK method: stock.compareAndSet
export const setStockRequest = requestAction(SET_STOCK_REQUEST);
export const setStockSuccess = successAction(SET_STOCK_SUCCESS);
export const setStockError = errorAction(SET_STOCK_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);
// Add extra data from additional pages
export const fetchExtraAvailabilityExceptionsSuccess = successAction(
  FETCH_EXTRA_EXCEPTIONS_SUCCESS
);

// SDK method: availabilityExceptions.create
export const addAvailabilityExceptionRequest = requestAction(ADD_EXCEPTION_REQUEST);
export const addAvailabilityExceptionSuccess = successAction(ADD_EXCEPTION_SUCCESS);
export const addAvailabilityExceptionError = errorAction(ADD_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
// Not successAction(): that helper expects result.data, but payout save dispatches with no SDK result.
export const savePayoutDetailsSuccess = () => ({ type: SAVE_PAYOUT_DETAILS_SUCCESS });
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);
export const savePayoutDetailsIdle = () => ({ type: SAVE_PAYOUT_DETAILS_IDLE });

// ================ Thunk ================ //

export function requestShowListing(actionPayload, config) {
  return (dispatch, getState, sdk) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(showListingsRequest(actionPayload));
    return sdk.ownListings
      .show({ ...actionPayload, ...queryParams })
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        dispatch(requestCommissionInfo({ listingId: actionPayload?.id?.uuid }));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

export function requestCommissionInfo({ listingId }) {
  return dispatch => {
    dispatch(requestCommissionInfoRequest({}));

    return getCommissionInfo({ listingId })
      .then(data => {
        dispatch(requestCommissionInfoSuccess(data));
        return data;
      })
      .catch(e => {
        return dispatch(requestCommissionInfoError(storableError(e)));
      });
  };
}

// Set stock if requested among listing update info
export function compareAndSetStock(listingId, oldTotal, newTotal) {
  return (dispatch, getState, sdk) => {
    dispatch(setStockRequest());

    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then(response => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        dispatch(setStockSuccess(response));
      })
      .catch(e => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return dispatch(setStockError(storableError(e)));
      });
  };
}

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export function requestCreateListingDraft(data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(createListingDraftRequest(data));
    const { stockUpdate, images, ...rest } = data;

    // Ser custom price for free booking to avoid changing existing process which require to have any price
    // See DECISIONS.md for more details
    // Tag: #initial-price-for-free-booking
    const initialPrice = new Money(1, 'USD');
    const isFreeProcess = rest?.publicData?.listingType === FREE_BOOKING_LISTING_TYPE;
    const priceOrCustomValue = isFreeProcess ? initialPrice : rest.price;
    const priceMaybe = rest.price || isFreeProcess ? { price: priceOrCustomValue } : {};

    // If images should be saved, create array out of the image UUIDs for the API call
    // Note: in this template, image upload is not happening at the same time as listing creation.
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingValues = withOnlineMeetingPrivacy({ ...imageProperty, ...rest, ...priceMaybe });

    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    let createDraftResponse = null;
    return sdk.ownListings
      .createDraft(ownListingValues, queryParams)
      .then(response => {
        createDraftResponse = response;
        const listingId = response.data.data.id;
        // If stockUpdate info is passed through, update stock
        return updateStockOfListingMaybe(listingId, stockUpdate, dispatch);
      })
      .then(() => {
        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(createDraftResponse));
        return createDraftResponse;
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
export function requestUpdateListing(tab, data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(updateListingRequest(data));
    const { id, stockUpdate, images, packageSync, ...rest } = data;

    const state = getState();
    const listingEntity = id?.uuid ? state.marketplaceData.entities.ownListing[id.uuid] : null;
    const existingPublicData = listingEntity?.attributes?.publicData;

    let mergedRest = { ...rest };
    if (
      tab === 'photos' &&
      mergedRest.publicData == null &&
      typeof images !== 'undefined' &&
      existingPublicData &&
      Object.keys(existingPublicData).length > 0
    ) {
      mergedRest = { ...mergedRest, publicData: { ...existingPublicData } };
    }

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingUpdateValues = withOnlineMeetingPrivacy(
      { id, ...imageProperty, ...mergedRest },
      listingEntity
    );
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    const existingTimeZone =
      state.marketplaceData.entities.ownListing[id.uuid]?.attributes?.availabilityPlan?.timezone;
    const includedTimeZone = rest?.availabilityPlan?.timezone;

    // Note: if update values include stockUpdate, we'll do that first
    // That way we get updated currentStock info among ownListings.update
    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => sdk.ownListings.update(ownListingUpdateValues, queryParams))
      .then(response => {
        dispatch(updateListingSuccess(response));
        dispatch(addMarketplaceEntities(response));
        dispatch(markTabUpdated(tab));

        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed.
        if (!!includedTimeZone && includedTimeZone !== existingTimeZone) {
          const searchString = '';
          const firstDayOfWeek = config.localization.firstDayOfWeek;
          const listing = response.data.data;
          fetchLoadDataExceptions(dispatch, listing, searchString, firstDayOfWeek);
        }

        if (packageSync) {
          return dispatch(syncListingPackages(packageSync)).then(() => response);
        }

        return response;
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

export const requestPublishListingDraft = listingId => (dispatch, getState, sdk) => {
  dispatch(publishListingRequest(listingId));

  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(response => {
      // Add the created listing to the marketplace data
      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

// Images return imageId which we need to map with previously generated temporary id
export function requestImageUpload(actionPayload, listingImageConfig, listingId) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    const imageVariantInfo = getImageVariantInfo(listingImageConfig);
    const queryParams = {
      expand: true,
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(uploadImageRequest(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const img = resp.data.data;
        const uploadSuccessAction = uploadImageSuccess({
          data: { ...img, id, imageId: img.id, file: actionPayload.file },
        });
        dispatch(uploadSuccessAction);

        // Auto-save images to draft listing
        //
        // RATIONALE:
        // Images are normally only attached to the listing when the user clicks "Publish" or "Save".
        // If the user uploads images but leaves before saving, the images are lost.
        // This block ensures images are persisted to the draft immediately after upload.
        //
        // SAFETY:
        // Wrapped in try-catch to ensure image upload success is not affected by auto-save errors.
        // If auto-save fails, we log it but still show the upload as successful to the user.
        try {
          if (listingId) {
            const state = getState();
            // Ensure listingId is a UUID string or object; check marketplaceData
            const listingUuid = listingId.uuid || listingId;
            const listing = state.marketplaceData.entities.ownListing?.[listingUuid];

            // Only auto-save for draft listings (published listings use the normal save flow)
            if (listing?.attributes?.state === LISTING_STATE_DRAFT) {
              const {
                uploadedImages,
                uploadedImagesOrder,
                removedImageIds,
              } = state.EditListingPage;
              // NOTE:
              // `marketplaceData.entities.ownListing[uuid]` is a *normalized* entity and often doesn't have
              // the denormalized `listing.images` array. However, `listing.relationships.images.data`
              // contains the currently attached image refs (ids), which is enough for update payload.
              const currentListingImages =
                listing.images || listing.relationships?.images?.data || [];

              // Get newly uploaded images from Redux state that aren't attached to listing yet
              const unattachedImages = uploadedImagesOrder
                .map(i => uploadedImages[i])
                .filter(Boolean);

              const allImages = currentListingImages.concat(unattachedImages);

              // Remove duplicates and filtered-out images
              const pickImagesAndIds = (imgs, img) => {
                const imgId = img.imageId || img.id;
                const shouldInclude =
                  !imgs.imageIds.includes(imgId) &&
                  !(removedImageIds && removedImageIds.includes(imgId));
                if (shouldInclude) {
                  imgs.imageEntities.push(img);
                  imgs.imageIds.push(imgId);
                }
                return imgs;
              };

              const uniqueImages = allImages.reduce(pickImagesAndIds, {
                imageEntities: [],
                imageIds: [],
              }).imageEntities;

              const updatePayload = {
                id: listingId,
                images: uniqueImages,
              };

              const minimalConfig = {
                layout: {
                  listingImage: listingImageConfig,
                },
              };

              // Dispatch update (fire and forget for auto-save)
              dispatch(requestUpdateListing('photos', updatePayload, minimalConfig));
            }
          }
        } catch (e) {
          log.error(e, 'auto-save-images-failed', { listingId });
          // Do not re-throw, let the image upload be successful
        }

        return uploadSuccessAction;
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const requestAddAvailabilityException = params => (dispatch, getState, sdk) => {
  const { listingId, start, end, seats, repeatWeeklyCount = 1 } = params;
  const validRepeatWeeklyCount = repeatWeeklyCount > 12 ? 12 : repeatWeeklyCount;

  // Generate array of exception ranges based on repeatWeeklyCount
  const exceptionRanges = Array.from({ length: validRepeatWeeklyCount }, (_, index) => {
    if (index === 0) return { start, end };

    // Add 7 days for each repeat
    const weekOffset = index * 7 * 24 * 60 * 60 * 1000;
    return {
      start: new Date(start.getTime() + weekOffset),
      end: new Date(end.getTime() + weekOffset),
    };
  });

  dispatch(addAvailabilityExceptionRequest(params));
  dispatch(updateUserEvents(UserEvents.USER_EVENT_FIRST_AVAILABILITY_UPDATED));

  const toPayload = range => ({
    listingId,
    start: range.start,
    end: range.end,
    seats,
  });

  // Create all exceptions in sequence
  return exceptionRanges
    .reduce((promise, range) => {
      return promise.then(() =>
        sdk.availabilityExceptions.create(toPayload(range), { expand: true }).then(response => {
          const availabilityException = response.data.data;
          dispatch(addAvailabilityExceptionSuccess({ data: availabilityException }));
        })
      );
    }, Promise.resolve())
    .catch(error => {
      dispatch(addAvailabilityExceptionError({ error: storableError(error) }));
      throw error;
    });
};

export const requestDeleteAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(deleteAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .delete(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return dispatch(deleteAvailabilityExceptionSuccess({ data: availabilityException }));
    })
    .catch(e => {
      dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestFetchAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  const { listingId, start, end, timeZone, page, isWeekly } = params;
  const fetchParams = { listingId, start, end };
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };
  dispatch(fetchAvailabilityExceptionsRequest(timeUnitIdProp));

  return sdk.availabilityExceptions
    .query(fetchParams)
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response);

      // Fetch potential extra exceptions pagination pages per month.
      // In theory, there could be several pagination pages worth of exceptions,
      // if range is month and unit is 'hour': 31 days * 24 hour = 744 slots for exceptions.
      const totalPages = response.data.meta.totalPages;
      if (totalPages > 1 && !page) {
        const extraPages = getArrayOfNItems(totalPages);

        // It's unlikely that this code is reached with default units.
        // Note:
        //  - Firing multiple API calls might hit API rate limit
        //    (This is very unlikely with this query and 'hour' unit.)
        //  - TODO: this doesn't take care of failures of those extra calls
        Promise.all(
          extraPages.map(page => {
            return sdk.availabilityExceptions.query({ ...fetchParams, page });
          })
        ).then(responses => {
          const denormalizedFlatResults = (all, r) => all.concat(denormalisedResponseEntities(r));
          const exceptions = responses.reduce(denormalizedFlatResults, []);
          dispatch(
            fetchExtraAvailabilityExceptionsSuccess({
              data: { ...timeUnitIdProp, exceptions },
            })
          );
        });
      }

      return dispatch(
        fetchAvailabilityExceptionsSuccess({
          data: { ...timeUnitIdProp, exceptions: availabilityExceptions },
        })
      );
    })
    .catch(e => {
      return dispatch(
        fetchAvailabilityExceptionsError({ ...timeUnitIdProp, error: storableError(e) })
      );
    });
};

// Helper function for loadData call.
const fetchLoadDataExceptions = (dispatch, listing, search, firstDayOfWeek) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone = listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const locationSearch = parse(search);
    const selectedDate = locationSearch?.d
      ? parseDateFromISO8601(locationSearch.d, timezone)
      : todayInListingsTZ;
    const startOfWeek = getStartOfWeek(selectedDate, timezone, firstDayOfWeek);
    const prevWeek = getStartOf(startOfWeek, 'day', timezone, -7, 'days');
    const nextWeek = getStartOf(startOfWeek, 'day', timezone, 7, 'days');
    const nextAfterNextWeek = getStartOf(nextWeek, 'day', timezone, 7, 'days');

    const nextMonth = getStartOf(todayInListingsTZ, 'month', timezone, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', timezone, 1, 'months');
    const horizonEnd = endOfAvailabilityExceptionRange(timezone, todayInListingsTZ);

    const sharedData = { listingId, timeZone: timezone };

    // Fetch data for selected week and nearest weeks for WeeklyCalendar
    // Plus current month and month after that for EditListingAvailabilityForm
    // Plus the full bookable horizon so "No sessions scheduled" sees slots ~6+ weeks out.
    //
    // NOTE: This is making 6 different Thunk calls, which update store 2 times each
    //       It would make sense to make on thunk function that fires 6 sdk calls/promises,
    //       but for the time being, it's clearer to push all the calls through
    //       requestFetchAvailabilityExceptions
    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: prevWeek,
          end: startOfWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: startOfWeek,
          end: nextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: nextWeek,
          end: nextAfterNextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: nextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: nextMonth,
          end: nextAfterNextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: horizonEnd,
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  dispatch(savePayoutDetailsRequest());

  if (values?.payoutPath === 'alternative' || values?.payoutPath === 'alternativeClear') {
    const alternativePayout = alternativePayoutFromFormValues(values);
    const previous = getState()?.user?.currentUser?.attributes?.profile?.protectedData
      ?.alternativePayout;
    const isUpdate = !!(previous && values.payoutPath !== 'alternativeClear');

    return sdk.currentUser
      .updateProfile(
        buildAlternativePayoutProfileUpdate(alternativePayout),
        ALTERNATIVE_PAYOUT_UPDATE_QUERY
      )
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];
        dispatch(currentUserShowSuccess(currentUser));

        if (values.payoutPath === 'alternativeClear') {
          dispatch(savePayoutDetailsIdle());
          return currentUser;
        }

        if (values.payoutPath === 'alternative' && alternativePayout) {
          const profile = currentUser?.attributes?.profile || {};
          return notifyAlternativePayoutApi({
            isUpdate,
            displayName: profile.displayName,
            payoutSummary: formatAlternativePayoutForEmail(alternativePayout),
          })
            .catch(notifyError => {
              log.error(notifyError, 'alternative-payout-notify-failed', {
                country: alternativePayout.country,
                method: alternativePayout.method,
              });
            })
            .then(() => {
              dispatch(savePayoutDetailsSuccess());
              showToast({ messageKey: 'Toast.alternativePayoutSaved' });
              return currentUser;
            });
        }

        dispatch(savePayoutDetailsSuccess());
        return currentUser;
      })
      .catch(e => {
        log.error(e, 'save-alternative-payout-failed', {
          payoutPath: values.payoutPath,
          country: values.country,
          method: values.payoutMethod,
        });
        dispatch(savePayoutDetailsError());
        throw e;
      });
  }

  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      if (!values?.recordUserPayoutChoice) {
        dispatch(savePayoutDetailsSuccess());
        return response;
      }
      return sdk.currentUser
        .updateProfile(buildUserStripePayoutChoiceUpdate(), ALTERNATIVE_PAYOUT_UPDATE_QUERY)
        .then(profileResponse => {
          const entities = denormalisedResponseEntities(profileResponse);
          if (entities.length === 1) {
            dispatch(currentUserShowSuccess(entities[0]));
          }
          dispatch(savePayoutDetailsSuccess());
          return response;
        })
        .catch(choiceError => {
          log.error(choiceError, 'save-user-stripe-choice-failed', {
            country: values.country,
          });
          dispatch(savePayoutDetailsSuccess());
          return response;
        });
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  dispatch(clearPublishError());
  const { id, type } = params;

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser())])
      .then(response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = { id: new UUID(id) };
  return Promise.all([dispatch(requestShowListing(payload, config)), dispatch(fetchCurrentUser())])
    .then(response => {
      const currentUser = getState().user.currentUser;
      if (currentUser && currentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }

      // Because of two dispatch functions, response is an array.
      // We are only interested in the response from requestShowListing here,
      // so we need to pick the first one
      const listing = response[0]?.data?.data;
      const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
      if (listing && isBookingProcessAlias(transactionProcessAlias)) {
        fetchLoadDataExceptions(dispatch, listing, search, config.localization.firstDayOfWeek);
      }

      return response;
    })
    .catch(e => {
      throw e;
    });
};
